import React, { useState, useEffect } from 'react';
import SwitchWithInput from '../SwitchWithInput';
import LabelFormatting from './LabelFormatting';
import { useDrop } from 'react-dnd';
import { Box, Typography } from '@mui/material';
import { RootState } from '../../../redux';
import { useDispatch, useSelector } from 'react-redux';
import {
  callQueryAPI,
  enableDisableTooltipChartControlSwitch,
  updateFormatOption,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { ChartConLabelFormates } from '../../../redux/ChartPoperties/ChartControlsInterface';
import { updateFormatForDm } from '../../../redux/DynamicMeasures/DynamicMeasuresActions';
import { setDisplayName } from '../../ChartAxes/setDisplayName';
import { setPrefix } from '../../ChartAxes/SetPrefix';
import {
  findNewDisplayName,
  getMeasureFieldsIncludingDateTimeGrain,
  getRoundingDigits,
} from '../../CommonFunctions/CommonFunctions';
import ExtraMeasureCard from '../../ChartAxes/Card/ExtraMeasureCard';
import { fontSize } from '../../..';
import ShortUniqueId from 'short-unique-id';

/**
 *
 * @returns UI of tooltip in chart setting
 */
interface TooltipFormatProps {
  iconColor?: string;
}

const TooltipFormat = ({ iconColor }: TooltipFormatProps) => {
  const { chartControls, chartProp, tabTileProps, dynamicMeasureState } = useSelector(
    (state: RootState) => ({
      chartControls: state.chartControls,
      chartProp: state.chartProperties,
      tabTileProps: state.tabTileProps,
      dynamicMeasureState: state.dynamicMeasuresState,
    }),
  );

  const dispatch = useDispatch();

  //-------- variable data specific to extra measure -----------//
  //only two extra fields are allowed, there is only one axis in extra measure and name is 'Measure' as its functionality is same as measure axis but display name will be different.
  const allowedNumbers = 2;
  let binName = 'Measure';
  let extraMeasureName = 'Extra_Measure';
  let bIndex = 0;
  //-----------------------------------------------------------//

  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
  const chartType = chartProp.properties[propKey]?.chartType;
  var dmKey = `${dynamicMeasureState.selectedTileId}.${dynamicMeasureState.selectedDynamicMeasureId}`;

  const chartAxes = chartProp?.properties[propKey]?.chartAxes;
  const selectedDs = chartProp.properties[propKey].selectedDs;
  let measureChartFields: any[] = [];
  const response = getMeasureFieldsIncludingDateTimeGrain(chartAxes);
  measureChartFields = [...response];

  let currentChartAxes =
    chartControls.properties[propKey]?.formatOptions?.tooltipFormats?.extraMeasureField;

  const checkedMeasure =
    chartControls.properties[propKey]?.formatOptions?.tooltipFormats?.checkedMeasure;
  const isEnabled = chartControls.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable;

  const selectedMeasure =
    chartControls.properties[propKey]?.formatOptions?.tooltipFormats?.selectedMeasure;
  const extraMeasureFieldsList =
    chartControls.properties[propKey]?.formatOptions?.tooltipFormats?.extraMeasureField[0]?.fields;

  let formatObject: ChartConLabelFormates | any;
  formatObject =
    chartType === 'richText'
      ? dynamicMeasureState.dynamicMeasureProps?.[`${dynamicMeasureState.selectedTabId}`]?.[
          `${dynamicMeasureState.selectedTileId}`
        ]?.[dmKey]?.formatOptions.tooltipFormats
      : chartControls.properties[propKey].formatOptions.tooltipFormats;

  const selectedDynamicMeasureProps =
    dynamicMeasureState?.dynamicMeasureProps?.[dynamicMeasureState.selectedTabId]?.[
      dynamicMeasureState.selectedTileId
    ]?.[`${dynamicMeasureState.selectedTileId}.${dynamicMeasureState.selectedDynamicMeasureId}`];

  let uID = '';

  const filterAxis = chartAxes.find((axis: any) => axis.name === 'Filter');
  const dimensionAxis = chartAxes.find((axis: any) => axis.name === 'Dimension');

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ['card', 'calculation'],
    canDrop: (item: any) => {
      if (!item || !item.bIndex) return false;
      return item?.bIndex === 99;
    },
    drop: (item) => {
      if (item?.bIndex === 99) {
        handleDrop(item);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  //change default rounding digits
  //for all count operation and integers rounding digits will be 0, for decimals it will be 2

  const handleUpdateFormat = (option: string, value: any, optionKey?: string) => {
    if (chartType === 'richText') {
      dispatch(updateFormatForDm(dmKey, option, value));
    } else {
      dispatch(updateFormatOption(propKey, optionKey, option, value));
    }
    dispatch(callQueryAPI(propKey, true));
  };

  const uIdGenerator = () => {
    const uId = new ShortUniqueId({ length: 4 });
    return uId();
  };

  async function handleDrop(item: any) {
    //----------------change display name---------------------------//
    const newFieldData = setDisplayName(
      setPrefix(item.fieldData, binName, chartType),
      binName,
      chartType,
    );
    newFieldData.originalDataSetId = selectedDs.id;
    newFieldData.originalTableId = item.fieldData.tableId;

    newFieldData.displayname = findNewDisplayName(
      chartProp.properties[propKey].chartAxes,
      newFieldData,
      formatObject,
    );
    const uID = uIdGenerator();
    newFieldData.uId = uID;

    if (item.boxUUID) {
      // newFieldData.displayName2 = newFieldData.displayname; //may be not needed
      newFieldData.SavedCalculationUUID = item.boxUUID;
      newFieldData.isCalculatedField = item.fieldData.isCalculatedField || true;
      newFieldData.isAggregated = item.fieldData.isAggregated || false;
    }

    //---------------add extra measure field in measure format and updating extraMeasureField in a particular measure-----------//
    const tempExtraMeasureFieldsList = JSON.parse(JSON.stringify(extraMeasureFieldsList));

    if (extraMeasureFieldsList?.length === allowedNumbers) {
      //---------------------------add new and remove lastly added field-------------------------------------//
      tempExtraMeasureFieldsList.splice(extraMeasureFieldsList.length - 1, 1);
    }
    tempExtraMeasureFieldsList.push(newFieldData);
    handleUpdateFormat(
      'extraMeasureField',
      [{ name: binName, fields: tempExtraMeasureFieldsList }],
      'tooltipFormats',
    );
    const defaultRoundingDigits = getRoundingDigits(newFieldData);
    handleUpdateFormat(
      'measureFormats',
      {
        ...formatObject.measureFormats,
        [newFieldData?.uId]: {
          formatValue: 'Number',
          currencySymbol: '$',
          enableRounding: true,
          roundingDigits: defaultRoundingDigits,
          numberSeparator: 'Abbrev',
          percentageCalculate: false,
        },
        [formatObject.selectedMeasure?.uId]: {
          ...formatObject.measureFormats[selectedMeasure?.uId],
        },
      },
      'tooltipFormats',
    );
  }

  const toggleSwitch = () => {
    dispatch(enableDisableTooltipChartControlSwitch(propKey, !isEnabled));
  };

  const isDateType = (uId: string) => {
    const newChartAxes = chartAxes.filter(
      (axis: any) =>
        axis.name !== 'Filter' &&
        axis.name !== 'Measure' &&
        axis.name !== 'X Axis' &&
        axis.name !== 'Y Axis',
    );
    for (const axis of newChartAxes) {
      for (const field of axis.fields || []) {
        if (field.uId === uId && field.timeGrain === 'date') {
          return true;
        }
      }
    }
    return false;
  };

  /**
   * This function handles checkbox click. When we check or uncheck then it updates the new checked list in tooltip formats accordingly.
   * @returns new checked list for tooltip formats.
   */
  const handleCheckboxChange = (uId: string) => {
    const isChecked = checkedMeasure.includes(uId);
    const newMeasureFormats = { ...formatObject.measureFormats };
    let newChecked;
    if (isChecked) {
      newChecked = checkedMeasure.filter((id: string) => id !== uId);
      delete newMeasureFormats[uId];
      handleUpdateFormat('measureFormats', newMeasureFormats, 'tooltipFormats');
      handleUpdateFormat('checkedMeasure', newChecked, 'tooltipFormats');
    } else {
      newChecked = [...checkedMeasure, uId];
      handleUpdateFormat('checkedMeasure', newChecked, 'tooltipFormats');
      handleUpdateFormat(
        'measureFormats',
        {
          ...formatObject.measureFormats,
          [uId]: isDateType(uId)
            ? {
                formatValue: 'Date',
                finalDateFormat: formatObject.measureFormats[uId]?.finalDateFormat || '',
                selectedMeasureFormat: setDisplayName,
              }
            : {
                formatValue: 'Number',
                currencySymbol: '$',
                enableRounding: true,
                roundingDigits: 1,
                numberSeparator: 'Abbrev',
                percentageCalculate: false,
              },
        },
        'tooltipFormats',
      );
    }
    return newChecked;
  };

  /**
   *
   * @returns checkbox section
   */
  function MeasureWithCheckBoxes() {
    return (
      <div className="check-boxes" style={{ paddingLeft: '0.6rem' }}>
        {measureChartFields?.map((field: any) => {
          const uniqueId = `tooltip-measure-${field.uId}`;
          return (
            <div
              style={{
                marginBottom: '1px',
                fontSize: '13px',
                display: 'flex',
                gap: '0.4rem',
                alignItems: 'center',
              }}
            >
              <input
                // className="checkbox_tick"
                type="checkbox"
                id={uniqueId}
                value={field.uId}
                checked={checkedMeasure.includes(field.uId)}
                onChange={() => handleCheckboxChange(field.uId)}
                disabled={
                  checkedMeasure.length === 1 && checkedMeasure.includes(field.uId) ? true : false
                }
                style={{
                  accentColor: 'rgb(150 146 146)',
                  verticalAlign: 'middle',
                  transform: 'scale(0.87)',
                  marginLeft: 0,
                  marginRight: 0,
                  pointerEvents:
                    checkedMeasure.length === 1 && checkedMeasure.includes(field.uId)
                      ? 'none'
                      : 'auto',
                  cursor:
                    checkedMeasure.length === 1 && checkedMeasure.includes(field.uId)
                      ? 'none'
                      : 'pointer',
                }}
              />
              <label htmlFor={uniqueId}>{field.displayname}</label>
            </div>
          );
        })}
      </div>
    );
  }

  // To get all the fields for tooltip format dropdown
  let checkedMeasureObjects: any[] = [];
  chartAxes.slice(1).forEach((axis: any) => {
    if (axis && Array.isArray(axis.fields)) {
      axis.fields.forEach((field: any) => {
        if (axis.name === 'Measure' || axis.name === 'X Axis' || axis.name === 'Y Axis') {
          if (checkedMeasure.includes(field.uId) && field.displayname) {
            checkedMeasureObjects.push(field);
          }
        } else {
          if (
            checkedMeasure.includes(field.uId) &&
            (field.dataType === 'timestamp' || field.dataType === 'date') &&
            field.timeGrain === 'date'
          ) {
            if (field.displayname) {
              checkedMeasureObjects.push(field);
            }
          }
        }
      });
    }
  });

  const extraMeasures =
    extraMeasureFieldsList && Array.isArray(extraMeasureFieldsList) ? extraMeasureFieldsList : [];
  // const allMeasuresForDropdown = [...checkedMeasureObjects, ...extraMeasures];
  const shouldIncludeExtraMeasures = chartType !== 'boxPlot' && chartType !== 'sankey';
  const allMeasuresForDropdown = shouldIncludeExtraMeasures
    ? [...checkedMeasureObjects, ...extraMeasures]
    : checkedMeasureObjects;

  return (
    <>
      <div
        className="optionDescription"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          overflowX: 'hidden',
        }}
      >
        <span style={{ color: iconColor ? iconColor : '#404040' }}>TOOLTIP</span>
        <Box
          sx={{
            width: '50px',
            height: '24px',
            display: 'flex',
            transform: 'translateX(62px)',
            marginBottom: '2px',
            '@supports (-moz-appearance: none)': {
              transform: 'translateX(56px)',
            },
          }}
        >
          <SwitchWithInput isChecked={isEnabled} onSwitch={toggleSwitch} />
        </Box>
      </div>
      {isEnabled && (
        <>
          <div
            style={{
              fontWeight: 600,
              // margin: '0.5rem 0 0.25rem 0',
              fontSize: '12px',
              textAlign: 'left',
              marginLeft: '0.6rem',
              marginBottom: '0.35rem',
              marginTop: '0.35rem',
              color: iconColor ? iconColor : '#515050',
            }}
          >
            Available Measures:
          </div>
          <MeasureWithCheckBoxes />
          {chartType !== 'sankey' && chartType !== 'boxPlot' && (
            <Box>
              <h4
                style={{
                  borderTop: '1px solid rgb(211,211,211)',
                  margin: '1rem 5% 0.6rem 4%',
                  //marginBottom: '6px',
                  paddingTop: '1rem',
                  textAlign: 'left',
                  fontSize: '12px',
                  color: iconColor ? iconColor : '#515050',
                }}
              >
                Add More Measures
                <span style={{ marginLeft: '5px' }} className="axisInfo">
                  ({extraMeasureFieldsList.length} of {allowedNumbers})
                </span>
              </h4>
              <Box
                ref={drop}
                style={{
                  border: '1px solid transparent',
                  minHeight: extraMeasureFieldsList?.length >= 1 ? '4.5rem' : '3.125rem',
                  height: 'auto',
                  margin: '1rem 4% 0.6rem',
                  textAlign: 'left',
                  borderBottom: '1px solid rgb(211,211,211)',
                }}
                data-testId="Tooltip_Measure_Dropzone"
                className={`dropzone ${isOver && canDrop ? 'dropzone-highlighted' : ''}`}
              >
                {extraMeasureFieldsList?.length > 0 &&
                extraMeasureFieldsList?.length <= allowedNumbers ? (
                  chartType === 'richText' ? (
                    selectedDynamicMeasureProps?.chartAxes[bIndex]?.fields?.map(
                      (field: any, index: number) => (
                        <ExtraMeasureCard
                          key={index}
                          field={field}
                          uID={uID || ''}
                          bIndex={bIndex}
                          axisTitle={binName}
                          itemIndex={index}
                          propKey={`${dynamicMeasureState.selectedDynamicMeasureId}.${dynamicMeasureState.selectedTileId}`}
                          formatObject={formatObject}
                          filterAxis={filterAxis}
                          dimensionAxis={dimensionAxis}
                          binName={binName}
                          extraMeasureName={extraMeasureName}
                        />
                      ),
                    )
                  ) : (
                    currentChartAxes[bIndex]?.fields?.map((field: any, index: number) => (
                      <ExtraMeasureCard
                        field={field}
                        uID={uID || ''}
                        bIndex={bIndex}
                        axisTitle={binName}
                        key={index}
                        itemIndex={index}
                        propKey={propKey}
                        formatObject={formatObject}
                        filterAxis={filterAxis}
                        dimensionAxis={dimensionAxis}
                        binName={binName}
                        extraMeasureName={extraMeasureName}
                      />
                    ))
                  )
                ) : (
                  <Typography
                    sx={{
                      color: '#484848',
                      fontSize: fontSize.extraSmall,
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                      height: '100%',
                      whiteSpace: 'pre-line',
                      lineHeight: '1.2rem',
                    }}
                  >
                    Drag and drop{'\n'}measure field here
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          <Box className="optionsInfo">
            <LabelFormatting formatFor="tooltip" measuresListForTooltip={allMeasuresForDropdown} />
          </Box>
        </>
      )}
    </>
  );
};

//temp notes for myself
//field is the field data in chartAxes, bIndex is the index number of axis, itemIndex is the index/position of item in a axis, axisTitle is the tile name of axis(ex- dimension, measure), uID is ''(check other cases)

export default TooltipFormat;
